import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as EventoPonteiro,
} from 'react';
import { createPortal } from 'react-dom';

interface Props {
  aberto: boolean;
  imagem: string | null;
  alt: string;
  titulo?: string;
  onFechar: () => void;
}

interface Ponto {
  x: number;
  y: number;
}

const ESCALA_MIN = 1;
const ESCALA_MAX = 4;
const ESCALA_DUPLO = 2.5;
const ARRASTO_FECHAR = 120;

function limitar(valor: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, valor));
}

// Visualizador de arte em tela cheia: fundo preto, arte inteira (object-contain),
// pinça para zoom, toque duplo para aproximar/afastar, arrastar quando ampliado,
// arrastar para baixo para fechar e o Voltar do celular (passo no histórico).
// Renderiza no body (portal) e trava a rolagem do fundo.
export default function VisualizadorArte({ aberto, imagem, alt, titulo, onFechar }: Props) {
  const areaRef = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(1);
  const [pos, setPos] = useState<Ponto>({ x: 0, y: 0 });
  const [arrasto, setArrasto] = useState(0);
  const [animar, setAnimar] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const atual = useRef({ escala: 1, x: 0, y: 0 });
  const ponteiros = useRef(new Map<number, Ponto>());
  const gesto = useRef({
    modo: 'pan' as 'pan' | 'pinch' | 'drag',
    dist: 0,
    escala: 1,
    px: 0,
    py: 0,
    sx: 0,
    sy: 0,
    tempo: 0,
  });
  const ultimoToque = useRef(0);

  useEffect(() => {
    atual.current = { escala, x: pos.x, y: pos.y };
  }, [escala, pos]);

  // Abriu: reseta o zoom e empurra um passo no histórico (Voltar fecha só ele).
  useEffect(() => {
    if (!aberto) return;
    setEscala(1);
    setPos({ x: 0, y: 0 });
    setArrasto(0);
    setAnimar(false);

    window.history.pushState({ arte: true }, '');
    const aoVoltar = () => onFechar();
    window.addEventListener('popstate', aoVoltar);
    return () => {
      window.removeEventListener('popstate', aoVoltar);
      // Se fechou pelo X/arrasto (não pelo Voltar), tira o passo do histórico.
      if (window.history.state && window.history.state.arte) {
        window.history.back();
      }
    };
  }, [aberto, onFechar]);

  // Trava a rolagem do fundo enquanto está aberto.
  useEffect(() => {
    if (!aberto) return;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    const aoEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    window.addEventListener('keydown', aoEsc);
    return () => window.removeEventListener('keydown', aoEsc);
  }, [aberto, onFechar]);

  const aoPressionar = useCallback((e: EventoPonteiro<HTMLDivElement>) => {
    setAnimar(false);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* alguns navegadores não permitem: segue sem captura */
    }
    ponteiros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesto.current;
    if (ponteiros.current.size === 1) {
      g.sx = e.clientX;
      g.sy = e.clientY;
      g.px = atual.current.x;
      g.py = atual.current.y;
      g.tempo = Date.now();
      g.modo = atual.current.escala > 1 ? 'pan' : 'drag';
    } else if (ponteiros.current.size === 2) {
      const [a, b] = Array.from(ponteiros.current.values());
      g.dist = Math.hypot(a.x - b.x, a.y - b.y);
      g.escala = atual.current.escala;
      g.modo = 'pinch';
    }
  }, []);

  const aoMover = useCallback((e: EventoPonteiro<HTMLDivElement>) => {
    if (!ponteiros.current.has(e.pointerId)) return;
    ponteiros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesto.current;

    if (ponteiros.current.size >= 2 && g.modo === 'pinch') {
      const [a, b] = Array.from(ponteiros.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const nova = limitar((g.escala * dist) / (g.dist || 1), ESCALA_MIN, ESCALA_MAX);
      setEscala(nova);
      if (nova <= 1.001) setPos({ x: 0, y: 0 });
      return;
    }

    if (ponteiros.current.size === 1) {
      const dx = e.clientX - g.sx;
      const dy = e.clientY - g.sy;
      if (g.modo === 'pan') {
        const area = areaRef.current;
        const maxX = area ? (area.clientWidth * (atual.current.escala - 1)) / 2 : 9999;
        const maxY = area ? (area.clientHeight * (atual.current.escala - 1)) / 2 : 9999;
        setPos({
          x: limitar(g.px + dx, -maxX, maxX),
          y: limitar(g.py + dy, -maxY, maxY),
        });
      } else if (g.modo === 'drag' && dy > 0) {
        setArrasto(dy);
      }
    }
  }, []);

  const aoSoltar = useCallback(
    (e: EventoPonteiro<HTMLDivElement>) => {
      const g = gesto.current;
      const dx = e.clientX - g.sx;
      const dy = e.clientY - g.sy;
      const toqueCurto =
        g.modo === 'drag' &&
        Math.abs(dx) < 8 &&
        Math.abs(dy) < 8 &&
        Date.now() - g.tempo < 300;

      ponteiros.current.delete(e.pointerId);

      if (ponteiros.current.size === 0) {
        if (g.modo === 'drag' && dy > ARRASTO_FECHAR) {
          setArrasto(0);
          g.modo = 'pan';
          onFechar();
          return;
        }
        if (toqueCurto) {
          const agora = Date.now();
          if (agora - ultimoToque.current < 300) {
            const nova = atual.current.escala > 1 ? 1 : ESCALA_DUPLO;
            setAnimar(true);
            setEscala(nova);
            setPos({ x: 0, y: 0 });
            ultimoToque.current = 0;
          } else {
            ultimoToque.current = agora;
          }
        }
        setArrasto(0);
        g.modo = 'pan';
        return;
      }

      if (ponteiros.current.size === 1) {
        const [restante] = Array.from(ponteiros.current.values());
        g.sx = restante.x;
        g.sy = restante.y;
        g.px = atual.current.x;
        g.py = atual.current.y;
        g.modo = atual.current.escala > 1 ? 'pan' : 'drag';
        g.tempo = Date.now();
        setArrasto(0);
      }
    },
    [onFechar],
  );

  const compartilhar = useCallback(async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: titulo ?? 'Evento', url });
        return;
      } catch {
        /* cai no copiar */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* silencioso */
    }
  }, [titulo]);

  if (!aberto || !imagem || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-black"
      role="dialog"
      aria-modal="true"
      aria-label={titulo ?? 'Arte do evento'}
    >
      <div
        className="relative z-10 flex items-center gap-2 px-4 py-3"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <p className="min-w-0 flex-1 truncate text-[15px] font-bold text-white/90">{titulo}</p>
        <button
          type="button"
          onClick={compartilhar}
          aria-label="Compartilhar"
          className="aperta flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md"
        >
          <i className={copiado ? 'ri-check-line text-xl' : 'ri-share-forward-line text-xl'} aria-hidden />
        </button>
        <button
          type="button"
          onClick={onFechar}
          aria-label="Fechar"
          className="aperta flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md"
        >
          <i className="ri-close-line text-2xl" aria-hidden />
        </button>
      </div>

      <div
        ref={areaRef}
        className="relative flex-1 overflow-hidden"
        style={{ touchAction: 'none' }}
        onPointerDown={aoPressionar}
        onPointerMove={aoMover}
        onPointerUp={aoSoltar}
        onPointerCancel={aoSoltar}
      >
        <div
          className="flex h-full w-full items-center justify-center"
          style={{
            transform: `translateY(${arrasto}px) scale(${Math.max(0.9, 1 - arrasto / 1400)})`,
            transition: arrasto === 0 ? 'transform 200ms ease' : 'none',
          }}
        >
          <img
            src={imagem}
            alt={alt}
            draggable={false}
            className="max-h-full max-w-full select-none object-contain"
            style={{
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${escala})`,
              transition: animar ? 'transform 200ms ease' : 'none',
            }}
          />
        </div>
      </div>

      <p
        className="relative z-10 text-center text-[12px] text-white/50"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        Pinça para ampliar · toque duplo · arraste para baixo para fechar
      </p>
    </div>,
    document.body,
  );
}